import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

/* Helper Methods */
const isMobile = () => {
  return matchMedia('(max-width: 639px)').matches;
};

/* method to turn marketData into valid data source */
const getMarkerSourceData = function(mData) {
  return {
    type: 'FeatureCollection',
    features: mData.map(m => {
      return {
        type: 'Feature',
        properties: {
          'id': m.id,
          'title': m.title,
          'countyState': m.countyState,
          'text': m.text,
          'acres': m.acres,
          'roads': m.roads,
          'power': m.power,
          'water': m.water,
          'terms': m.terms,
          'taxes': m.taxes,
          'linkUrl': m.linkUrl,
          'linkTarget': m.linkTarget,
          'markerImgUrl': m.markerImgUrl,
          'markerColour': m.markerColour,
          'markerBorderColour': m.markerBorderColour,
          'sidebarImage': m.sidebarImage,
          'auctionEnd': m.auctionEnd
        },
        geometry: {
          type: 'Point',
          coordinates: m.lngLat
        }
      };
    })
  };
};

const asNumber = function(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isValidLngLat = function(lng, lat) {
  return Number.isFinite(lng) && Number.isFinite(lat) &&
    lng >= -180 && lng <= 180 &&
    lat >= -90 && lat <= 90;
};

const normalizeLngLat = function(input) {
  if (!input) return null;

  let lng, lat;

  if (Array.isArray(input)) {
    lng = asNumber(input[0]);
    lat = asNumber(input[1]);
  } else if (typeof input === 'object') {
    lng = asNumber(input.lng);
    lat = asNumber(input.lat);
  }

  if (lng == null || lat == null) return null;

  // basic swap recovery (common bug)
  if (!isValidLngLat(lng, lat) && isValidLngLat(lat, lng)) {
    [lng, lat] = [lat, lng];
  }

  if (!isValidLngLat(lng, lat)) return null;
  return [lng, lat];
};

export function markeredMapbox(token, opts = {}, markerData = {}, plotData = {}) {
  mapboxgl.accessToken = token;

  /* Closure variables — kept out of Alpine's reactive proxy */
  let map = null;
  const allMarkers = {};

  const options = {
    cooperativeGestures: false,
    dragRotate: false,
    scrollZoom: false,
    attributionControl: false,
    projection: 'mercator',
    ... opts
  };

  return {
    zoom : null,
    resetZoom : false,
    sidebarIsOpen: false,
    sidebarProps: {},

    /**
     * Alpine calls destroy() when the component's root element leaves the
     * DOM (e.g. when Unpoly swaps [up-main]). Without this the Mapbox GL
     * instance — its WebGL context, per-frame `render` loop and listeners —
     * is orphaned, leaking contexts until the browser starts dropping them.
     */
    destroy() {
      if (map) {
        map.remove();
        map = null;
      }
    },

    resetMap() {
      this.sidebarIsOpen = false;
      this.setBounds(markerData, plotData);
    },

    showMarkerSidebar(props) {
      this.sidebarProps = props;
      this.sidebarIsOpen = true;
    },

    setBounds(mData, pData = {}, reset = false) {
      if(reset && options.center && options.zoom) {
        map.flyTo({
          center: opts.center,
          zoom: opts.zoom
        });
        return;
      }

      if(mData.length == 1) {
        const lngLat = normalizeLngLat(mData[0].lngLat);
        if (lngLat) {
          this.panToMarker(lngLat, {
            maxZoom : 2
          });
          return;
        }
      }

      let bounds = new mapboxgl.LngLatBounds();
      if(pData.length) {
        console.log('plot data found, setting bounds to plots');
        pData[0].forEach(p => {
          const lngLat = normalizeLngLat(p);
          if (!lngLat) {
            console.warn('Skipping invalid plot lngLat', p, pData);
            return;
          }
          bounds.extend(lngLat);
        });
      } else {
        console.log('marker data found, setting bounds to markers');
        mData.forEach(m => {
          let lngLat = normalizeLngLat(m.lngLat);
          if (!lngLat) {
            console.warn('Skipping invalid marker lngLat', lngLat, m);
            return;
          }
          bounds.extend(lngLat);
        });
      }

      if (bounds.isEmpty()) {
        if (options.center) {
          map.flyTo({ center: options.center, zoom: options.zoom });
        }
        return;
      }

      try {
        map.fitBounds(bounds, {
          padding: isMobile() ? 50 : 200
        });
      } catch(e) {
        console.log('unable to fit into screen; trying again without restriction');
        map.fitBounds(bounds);
      }
    },

    panToMarker(coords, opts) {
      var offsetX = ( isMobile() ) ? 0 : - this.$refs.sidebar.offsetWidth / 2;

      map.panTo( coords, {
        offset: [ offsetX, 0 ],
        ...opts
      } );
    },

    createMarker(props, coords) {
      const el = document.createElement('div');
      el.innerHTML = '<div class="marker" style="background-image:url('+props.markerImgUrl+');--marker-bg:'+props.markerColour+';--marker-border:'+props.markerBorderColour+';"></div>';

      el.addEventListener('click', () => {
        this.panToMarker(coords);
        this.showMarkerSidebar(props);
      }, false);

      return el;
    },

    init() {
      const container = this.$refs.map || null;

      if(!container) {
        console.log('map container not found');
      }

      options['container'] = container;
      map = new mapboxgl.Map(options);

      map.on('idle', () => {
        let zoom = map.getZoom();

        if(this.zoom === null) {
          this.zoom = zoom;
        }

        this.resetZoom = (this.zoom !== zoom);
      });

      map.on('load', () => {
        // Add a plot if there is one
        if(plotData) {
          map.addSource('plot', {
            'type': 'geojson',
            'data': {
              'type': 'Feature',
              'geometry': {
                'type': 'Polygon',
                'coordinates': plotData
              }
            }
          });

          // Add a new layer to visualize the polygon
          map.addLayer({
            'id': 'plot',
            'type': 'fill',
            'source': 'plot', // reference the data source
            'layout': {},
            'paint': {
              'fill-color': '#6fc1cb',
              'fill-opacity': 0.57
            }
          });

          // Add an outline around the polygon
          map.addLayer({
            'id': 'outline',
            'type': 'line',
            'source': 'plot',
            'layout': {},
            'paint': {
              'line-color': '#157b88',
              'line-width': 2
            }
          });
        }

        map.addSource('markers', {
          'type': 'geojson',
          'data': getMarkerSourceData(markerData),
          'cluster': true,
          'clusterRadius': 30
        });

        /** Part 1: Clusters
         * Clustering is added via Layers.
         * Since Layers can only be discrete types,
         * we have to add 2 layers, one for the count and one for the circle behind it
         */
        const clustersLayer = {
          id: 'clusters',
          type: 'circle',
          source: 'markers',
          filter: ['has', 'point_count'],
          paint: {
            'circle-radius': 26,
            'circle-color': '#149A82',
            'circle-stroke-color': '#0F7361',
            'circle-stroke-width': 1,
          }
        };

        const clusterCountLayer = {
          id: 'cluster-count',
          type: 'symbol',
          source: 'markers',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ["Suisse Int'l Book"],
            'text-size': 18,
          },
          paint: {
            'text-color': '#ffffff',
          }
        };

        // Now add the 2 cluster layers to the map
        map.addLayer(clustersLayer);
        map.addLayer(clusterCountLayer);

        // zoom to a cluster on click
        map.on('click', 'clusters', (e) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters']
          });
          const clusterId = features[0].properties.cluster_id;
          map.getSource('markers').getClusterExpansionZoom(
            clusterId,
            (err, zoom) => {
              if (err) return;

              this.sidebarIsOpen = false;

              map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom
              });
            }
          );
        });

        /** Part 2: Markers
         * Markers are added using custom HTML
         */

        let markersOnScreen = {}; // `let` because we overwrite it below

        // function to update all visible markers on the map,
        // called below every time the map renders
        const updateMarkers = () => {

          // local cache of any markers that are visible on this render
          const visibleMarkers = {};

          // this will return all "features", either clusters or markers,
          // that are currently visible on the map,
          // which we then filter out to only return markers.
          const features = map.querySourceFeatures('markers').filter((f) => !f.properties.cluster);

          // for every marker on the screen, create an HTML marker for it (if we didn't yet already),
          // and add it to the map if it's not there already
          for (const feature of features) {
            const coords = feature.geometry.coordinates;
            const props = feature.properties;
            const id = props.id;

            // create marker or retrieve from local cache
            let marker = allMarkers[id];
            if(!marker) {
              const el = this.createMarker(props, coords);
              marker = allMarkers[id] = new mapboxgl.Marker({
                element: el,
              }).setLngLat(coords);
            }

            // add to our local cache of markers that should be visible on this render
            visibleMarkers[id] = marker;

            if(!markersOnScreen[id]) marker.addTo(map);
          }

          // for every marker on screen from the previous render, remove those that are no longer visible
          for (const id in markersOnScreen) {
            if (!visibleMarkers[id]) markersOnScreen[id].remove();
          }

          // overwrite our cache of markers on screen with the visible set from this render
          markersOnScreen = visibleMarkers;
        };

        // after the GeoJSON data is loaded, update markers on the screen on every frame
        map.on('render', () => {
          if (!map.isSourceLoaded('markers')) return;
          updateMarkers();
        });

        // Map controls
        map.addControl(new mapboxgl.FullscreenControl(), 'bottom-left');
        map.addControl(new mapboxgl.NavigationControl({
          showCompass: false
        }), 'bottom-left');

        // Change cursor when hovering over clusters
        map.on('mouseenter', 'clusters', () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        // Reset cursor when not hovering over clusters
        map.on('mouseleave', 'clusters', () => {
          map.getCanvas().style.cursor = '';
        });

        // if(! options.center || ! options.zoom) {
          this.setBounds(markerData, plotData);
        // }
      });
    }
  };
};

export function plottedMapbox(token, opts = {}, plotData = {}) {
  mapboxgl.accessToken = token;

  /* Closure variable — kept out of Alpine's reactive proxy */
  let map = null;

  const options = {
    cooperativeGestures: false,
    dragRotate: false,
    scrollZoom: false,
    attributionControl: false,
    projection: 'mercator',
    ... opts
  };

  return {
    isSatellite: true,

    /**
     * Tear the Mapbox GL instance down when Alpine destroys the component
     * (e.g. on an Unpoly fragment swap). See markeredMapbox.destroy().
     */
    destroy() {
      if (map) {
        map.remove();
        map = null;
      }
    },

    addLayers() {
      map.addSource('plot', {
        'type': 'geojson',
        'data': {
          'type': 'Feature',
          'geometry': {
            'type': 'Polygon',
            'coordinates': plotData
          }
        }
      });

      // Add a new layer to visualize the polygon
      map.addLayer({
        'id': 'plot',
        'type': 'fill',
        'source': 'plot', // reference the data source
        'layout': {},
        'paint': {
          'fill-color': '#6fc1cb',
          'fill-opacity': 0.57
        }
      });

      // Add an outline around the polygon
      map.addLayer({
        'id': 'outline',
        'type': 'line',
        'source': 'plot',
        'layout': {},
        'paint': {
          'line-color': '#157b88',
          'line-width': 2
        }
      });
    },

    toggleStyle() {
      this.isSatellite = !this.isSatellite;
      const newStyle = this.isSatellite ? options.style : 'mapbox://styles/chrismorrissey/cmgawxwol00cl01qzckcfe4v8/draft';
      map.setStyle(newStyle);
    },

    init() {
      const container = this.$refs.map || null;

      if(!container) {
        console.log('map container not found');
      }

      options['container'] = container;
      map = new mapboxgl.Map(options);

      map.on('style.load', () => {
        this.addLayers();
      });

      // Map controls
      map.addControl(new mapboxgl.FullscreenControl(), 'bottom-left');
      map.addControl(new mapboxgl.NavigationControl({
        showCompass: false
      }), 'bottom-left');
    }
  };
};
